import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { getAuth, updateProfile, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { api } from '../utils/apiService';
import { API_CONFIG } from '../utils/config';

const PersonalProfileForm = ({ open, onClose }) => {
  const auth = getAuth();
  const storage = getStorage();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [avatar, setAvatar] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('PersonalProfile - Token exists:', !!token);
        console.log('PersonalProfile - API Base URL:', API_CONFIG.API_URL);
        
        if (!token) {
          console.warn('No auth token found');
          return;
        }

        console.log('PersonalProfile - Fetching user profile from:', `${API_CONFIG.API_URL}/users/profile`);
        const response = await api.get('/users/profile');
        console.log('PersonalProfile - Profile response:', response.data);

        const userData = response.data.user;
        setFormData(prev => ({
          ...prev,
          displayName: userData.username || '',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
        setAvatarUrl(userData.photoURL || '');
      } catch (error) {
        console.error('PersonalProfile - Error loading user data:', error);
        console.error('PersonalProfile - Error response:', error.response?.data);
        console.error('PersonalProfile - Request URL:', error.config?.url);
        console.error('PersonalProfile - Full URL:', error.config?.baseURL + error.config?.url);
        
        // Fallback to Firebase data if MongoDB data fetch fails
        if (auth.currentUser) {
          console.log('PersonalProfile - Using Firebase fallback data');
          setFormData(prev => ({
            ...prev,
            displayName: auth.currentUser.displayName || '',
            email: auth.currentUser.email || '',
            phoneNumber: '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          }));
          setAvatarUrl(auth.currentUser.photoURL || '');
        }
      }
    };

    if (open) {
      loadUserData();
    }
  }, [open]);

  const validateCurrentPassword = async (password) => {
    console.log('Validating current password...');
    if (!password) {
      console.log('Password is empty, clearing error');
      setPasswordError('');
      return;
    }

    // Check if user is authenticated
    if (!auth.currentUser) {
      console.log('No authenticated user, skipping password validation');
      setPasswordError('');
      return;
    }

    try {
      // For Google-authenticated users, we'll skip the password validation
      // since they don't have a password set
      if (auth.currentUser.providerData && auth.currentUser.providerData[0]?.providerId === 'google.com') {
        console.log('User is authenticated with Google, skipping password validation');
        setPasswordError('');
        return;
      }

      console.log('Creating credential with email:', auth.currentUser.email);
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        password
      );
      console.log('Attempting to reauthenticate...');
      await reauthenticateWithCredential(auth.currentUser, credential);
      console.log('Reauthentication successful');
      setPasswordError('');
    } catch (error) {
      console.error('Password validation error:', error);
      console.log('Error code:', error.code);
      console.log('Error message:', error.message);
      
      // If the user is authenticated with Google, don't show the error
      if (auth.currentUser?.providerData && auth.currentUser.providerData[0]?.providerId === 'google.com') {
        setPasswordError('');
      } else {
        setPasswordError('Current password is not correct');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('Input changed:', name, value ? 'value provided' : 'empty value');
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear password error when user starts typing again
    if (name === 'currentPassword') {
      console.log('Clearing password error on new input');
      setPasswordError('');
    }
  };

  const handleAvatarChange = (e) => {
    if (e.target.files[0]) {
      setAvatar(e.target.files[0]);
      setAvatarUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const uploadAvatar = async () => {
    if (avatar && auth.currentUser) {
      try {
        const storageRef = ref(storage, `avatars/${auth.currentUser.uid}`);
        await uploadBytes(storageRef, avatar);
        const url = await getDownloadURL(storageRef);
        await updateProfile(auth.currentUser, { photoURL: url });
        return url;
      } catch (error) {
        console.error('Error uploading avatar:', error);
        // Return current avatar URL if upload fails
        return avatarUrl;
      }
    }
    return avatarUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      console.log('PersonalProfile - Submit - Token exists:', !!token);
      
      if (!token) {
        throw new Error('You are not authenticated. Please log in again.');
      }

      // Upload avatar if changed
      const photoURL = await uploadAvatar();

      // Update Firebase profile if user is authenticated with Firebase
      if (auth.currentUser) {
        console.log('PersonalProfile - Updating Firebase profile');
        await updateProfile(auth.currentUser, {
          displayName: formData.displayName,
          photoURL,
        });

        // Update email in Firebase if changed
        if (formData.email !== auth.currentUser.email) {
          await updateEmail(auth.currentUser, formData.email);
        }

        // Update password in Firebase if provided and user is not Google-authenticated
        if (formData.newPassword && auth.currentUser.providerData && auth.currentUser.providerData[0]?.providerId !== 'google.com') {
          if (formData.newPassword !== formData.confirmPassword) {
            throw new Error('New passwords do not match');
          }
          await updatePassword(auth.currentUser, formData.newPassword);
        }
      } else if (formData.newPassword) {
        // If no Firebase user but password change requested, show warning
        console.warn('Cannot change password: No Firebase authentication');
        setMessage({
          type: 'warning',
          text: 'Password change is only available for Firebase authenticated users'
        });
      }

      // Update MongoDB profile (apiClient interceptor will add the token automatically)
      console.log('PersonalProfile - Updating MongoDB profile');
      const response = await api.put('/users/profile', {
        username: formData.displayName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        photoURL,
        currentPassword: formData.currentPassword || undefined,
        newPassword: formData.newPassword || undefined
      });
      console.log('PersonalProfile - Update response:', response.data);

      // Clear password fields after successful update
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));

      setMessage({
        type: 'success',
        text: 'Profile updated successfully!'
      });

      // Close the form after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('PersonalProfile - Error updating profile:', error);
      console.error('PersonalProfile - Error response:', error.response?.data);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Error updating profile'
      });
    } finally {
      setLoading(false);
    }
  };

  // Common styling for all textboxes
  const controlStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      height: 40,
      '& .MuiOutlinedInput-notchedOutline': {
        borderRadius: 0,
        borderColor: 'rgba(0, 0, 0, 0.12)'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(67, 97, 238, 0.3)'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#4361ee'
      }
    },
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      fontSize: '0.875rem',
      height: '24px',
      '&::placeholder': {
        opacity: 0.7,
        color: 'text.secondary'
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      color: 'text.secondary',
      transform: 'translate(12px, 11px)',
      '&.Mui-focused': {
        color: '#4361ee'
      }
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(12px, -8px) scale(0.75)'
    },
    mb: 1.5
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '4px',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          maxWidth: '500px',
          margin: 'auto'
        }
      }}
    >
      <DialogTitle sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          Personal Profile
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{ 
            p: 0.5,
            bgcolor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.08)' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ px: 2, py: 2 }}>
          {message.text && (
            <Alert severity={message.type} sx={{ mb: 2, fontSize: '0.875rem' }}>
              {message.text}
            </Alert>
          )}

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            mb: 3,
            mt: 1
          }}>
            <Avatar
              src={avatarUrl}
              sx={{ 
                width: 80, 
                height: 80, 
                mb: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="avatar-upload"
              type="file"
              onChange={handleAvatarChange}
            />
            <label htmlFor="avatar-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCameraIcon />}
                size="small"
                sx={{ 
                  borderRadius: 0,
                  mt: 1,
                  fontSize: '0.8rem'
                }}
              >
                Change Photo
              </Button>
            </label>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder="Enter your display name"
                sx={controlStyle}
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                sx={controlStyle}
                InputProps={{
                  readOnly: true,
                }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                sx={controlStyle}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ 
                mb: 1, 
                fontSize: '0.9rem', 
                fontWeight: 600,
                color: 'text.secondary'
              }}>
                Change Password
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                name="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={handleInputChange}
                onBlur={(e) => validateCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                sx={controlStyle}
                error={!!passwordError}
                helperText={passwordError}
              />
              <TextField
                fullWidth
                label="New Password"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter your new password"
                sx={controlStyle}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your new password"
                sx={controlStyle}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ 
              textTransform: 'none',
              borderRadius: 0,
              px: 3,
              minWidth: '80px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={loading}
            sx={{ 
              textTransform: 'none',
              borderRadius: 0,
              px: 3,
              minWidth: '80px',
              bgcolor: '#4361ee',
              '&:hover': {
                bgcolor: '#3451dd'
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PersonalProfileForm; 